import { gql, useQuery } from '@apollo/client';
import get from 'lodash/get';

export const GET_DEFAULT_PERMISSIONS = gql`
  query defaultPermissions {
    defaultPermissions {
      slug
      authorized
    }
  }
`;

export const FEE_PACKAGE_PERMISSIONS = gql`
  query feePackagePermissions {
    feePackagePermissions {
      permission
    }
  }
`;

export const ROLE_PERMISSIONS = gql`
  query RolePermission {
    rolePermission {
      role
    }
  }
`;

export const SKYHOOK_PERMISSIONS = gql`
  query SkyhookPermissions {
    skyhookPermissions {
      id: slug
      slug
      authorized
    }
  }
`;

export const ORGANIZATION_PERMISSIONS = gql`
  query OrganizationPermissions {
    organizationPermissions {
      id: slug
      slug
      authorized
    }
  }
`;

export const SUPPORT_PERMISSIONS = gql`
  query SupportPermissions {
    supportPermissions {
      permission
    }
  }
`;

export const useDefaultPermissions = () => {
  const { data, loading } = useQuery(GET_DEFAULT_PERMISSIONS);

  const defaultPermissions = get(data, 'defaultPermissions', []);
  return { defaultPermissions, loadingDefaultPermissions: loading };
};

export const useFeePackagePermissions = () => {
  const { data, loading } = useQuery(FEE_PACKAGE_PERMISSIONS);

  const feePackageLevel = get(data, 'feePackagePermissions', []);
  return { feePackageLevel, loadingFeePackageLevel: loading };
};

export const useRolePermissions = () => {
  const { data, loading } = useQuery(ROLE_PERMISSIONS);

  const rolePermissions = get(data, 'rolePermission', []);
  return { rolePermissions, loadingRolePermissions: loading };
};

export const useFetchOrganizationPermissions = () => {
  const { data, loading: fetchOrganizationPermissionsLoading } = useQuery(
    ORGANIZATION_PERMISSIONS
  );

  const permissions = get(data, 'organizationPermissions', []);

  const prettyPerms = prettyPermissions(permissions);

  const fetchOrganizationPermissions = {
    canUseAdminApp: get(prettyPerms, 'skyhook_can_use_app', false),
    canMockAll: get(prettyPerms, 'skyhook_can_mock_all', false),
    canMockAssigned: get(prettyPerms, 'skyhook_can_mock_assigned', false),
    canMockPartner: get(prettyPerms, 'skyhook_can_mock_partner', false),
    canViewFlock: get(prettyPerms, 'skyhook_can_view_flock', false),
    canViewPartner: get(prettyPerms, 'skyhook_can_view_partner', false),
    canViewTestInvite: get(prettyPerms, 'skyhook_can_send_test_invite', false),
    canUnlaunchGbm: get(prettyPerms, 'skyhook_can_unlaunch_gbm', false),
    canViewSkyhookButton: get(
      prettyPerms,
      'skyhook_can_view_skyhook_button',
      false
    ),
    canViewLocationFeatures: get(
      prettyPerms,
      'skyhook_can_view_location_features',
      false
    ),
    canViewOrganizationFeatures: get(
      prettyPerms,
      'skyhook_can_view_organization_features',
      false
    ),
    canDeleteSmsRequest: get(
      prettyPerms,
      'skyhook_can_delete_sms_request',
      false
    ),
    canArchiveWidget: get(
      prettyPerms,
      'webchat_can_archive_widget_in_any_org',
      false
    ),
    canArchiveOrgs: get(
      prettyPerms,
      'skyhook_can_archive_organizations',
      false
    ),
  };

  return { fetchOrganizationPermissions, fetchOrganizationPermissionsLoading };
};

export const useFetchSkyhookPermissions = () => {
  const { data, loading: fetchSkyhookPermissionsLoading } =
    useQuery(SKYHOOK_PERMISSIONS);

  const permissions = get(data, 'skyhookPermissions', []);

  const prettyPerms = prettyPermissions(permissions);

  const fetchSkyhookPermissionsData = {
    canUseAdminApp: get(prettyPerms, 'skyhook_can_use_app', false),
    canViewAllFlockTabs: get(
      prettyPerms,
      'skyhook_can_view_all_flock_tabs',
      false
    ),
    canMockAll: get(prettyPerms, 'skyhook_can_mock_all', false),
    canMockAssigned: get(prettyPerms, 'skyhook_can_mock_assigned', false),
    canMockPartner: get(prettyPerms, 'skyhook_can_mock_partner', false),
    canMockSupport: get(prettyPerms, 'skyhook_can_mock_support', false),
    canViewFlock: get(prettyPerms, 'skyhook_can_view_flock', false),
    canViewPartner: get(prettyPerms, 'skyhook_can_view_partner', false),
    canUseRogbot: get(prettyPerms, 'skyhook_can_use_rogbot', false),
    canGraduateLocation: get(
      prettyPerms,
      'skyhook_can_graduate_location',
      false
    ),
    canAssignUserToOrg: get(
      prettyPerms,
      'skyhook_can_assign_user_to_ocs',
      false
    ),
  };

  return {
    fetchSkyhookPermissionsData,
    fetchSkyhookPermissionsLoading,
  };
};

export const useFetchSupportPermissions = () => {
  const { data, loading: fetchSupportPermissionsLoading } =
    useQuery(SUPPORT_PERMISSIONS);
  const permissions = get(data, 'supportPermissions', []);

  const fetchSupportPermissionsData = permissions.reduce(
    (acc, { permission }) => {
      return { ...acc, [permission]: true };
    },
    {}
  );

  return {
    fetchSupportPermissionsData,
    fetchSupportPermissionsLoading,
  };
};

function prettyPermissions(permissions) {
  return permissions.reduce((prev, curr) => {
    prev[curr.slug] = curr.authorized;
    return prev;
  }, {});
}
